import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({
  session: null,
  user: null,
  loading: true,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data?.session ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email, password, meta = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback`, data: meta },
    });
    return { data, error };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = { session, user: session?.user ?? null, loading, signUp, signIn, signOut };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

// ── Waitlist ────────────────────────────────────────────────────────────────
// Writes to a `waitlist` table. See SETUP.md for the SQL.

export async function joinWaitlist(email, source = 'landing') {
  const clean = (email || '').trim().toLowerCase();
  if (!clean || !clean.includes('@')) {
    return { ok: false, error: 'That email address doesn\u2019t look right.' };
  }

  const { error } = await supabase.from('waitlist').insert({ email: clean, source });

  if (error) {
    // 23505 is a unique-constraint violation — they're already on the list,
    // which isn't a failure from the visitor's point of view.
    if (error.code === '23505') return { ok: true, already: true };
    return { ok: false, error: 'Something went wrong. Try again in a moment.' };
  }

  return { ok: true };
}
